/**
 * Task 34: Unified Toast Notification System
 * 
 * Provides consistent success/error/info alerts across the app.
 * Works uniformly on mobile and desktop.
 * 
 * Usage:
 *   import toast from '../utils/toast';
 *   toast.success('Action completed!');
 *   toast.error('Something went wrong');
 *   toast.info('FYI: ...');
 */

class ToastManager {
  constructor() {
    this.toasts = [];
    this.containerId = 'toast-container';
    this.maxToasts = 3;
    this.defaultDuration = 4000; // 4 seconds
    this.ensureContainer();
  }

  /**
   * Ensure toast container exists in DOM
   */
  ensureContainer() {
    if (document.getElementById(this.containerId)) return;

    const container = document.createElement('div');
    container.id = this.containerId;
    container.className = 'fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none';
    container.style.cssText = `
      position: fixed;
      top: 1rem;
      right: 1rem;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      pointer-events: none;
    `;
    
    // Mobile-responsive positioning
    if (window.innerWidth < 640) {
      container.style.left = '1rem';
      container.style.right = '1rem';
      container.style.top = '1rem';
    }

    document.body.appendChild(container);
  }

  /**
   * Show success toast
   */
  success(message, duration = this.defaultDuration) {
    return this.show({
      message,
      type: 'success',
      duration,
      icon: '✓',
      bgColor: 'bg-green-500',
      textColor: 'text-white',
    });
  }

  /**
   * Show error toast
   */
  error(message, duration = this.defaultDuration) {
    return this.show({
      message,
      type: 'error',
      duration,
      icon: '✕',
      bgColor: 'bg-red-500',
      textColor: 'text-white',
    });
  }

  /**
   * Show info toast
   */
  info(message, duration = this.defaultDuration) {
    return this.show({
      message,
      type: 'info',
      duration,
      icon: 'ℹ',
      bgColor: 'bg-blue-500',
      textColor: 'text-white',
    });
  }

  /**
   * Show warning toast
   */
  warning(message, duration = this.defaultDuration) {
    return this.show({
      message,
      type: 'warning',
      duration,
      icon: '⚠',
      bgColor: 'bg-yellow-500',
      textColor: 'text-white',
    });
  }

  /**
   * Core toast display logic
   */
  show(config) {
    this.ensureContainer();

    const toastId = `toast-${Date.now()}-${Math.random()}`;
    const toast = document.createElement('div');
    toast.id = toastId;
    toast.className = `
      ${config.bgColor} ${config.textColor}
      px-4 py-3 rounded-lg shadow-lg
      flex items-center gap-3
      pointer-events-auto
      animate-slide-in
      min-w-[280px] max-w-[400px]
      transition-all duration-300
    `.trim();

    toast.style.cssText = `
      animation: slideIn 0.3s ease-out;
      margin-bottom: 0.5rem;
    `;

    toast.innerHTML = `
      <span class="text-xl font-bold">${config.icon}</span>
      <span class="flex-1 text-sm font-medium">${this.escapeHtml(config.message)}</span>
      <button 
        class="text-white hover:opacity-75 transition-opacity ml-2"
        onclick="this.parentElement.remove()"
        aria-label="Close"
      >
        ✕
      </button>
    `;

    const container = document.getElementById(this.containerId);
    
    // Limit max toasts
    const existingToasts = container.querySelectorAll('div');
    if (existingToasts.length >= this.maxToasts) {
      existingToasts[0].remove();
    }

    container.appendChild(toast);

    // Auto-remove after duration
    if (config.duration > 0) {
      setTimeout(() => {
        if (document.getElementById(toastId)) {
          toast.style.animation = 'slideOut 0.3s ease-in';
          setTimeout(() => toast.remove(), 300);
        }
      }, config.duration);
    }

    return toastId;
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Remove specific toast
   */
  remove(toastId) {
    const toast = document.getElementById(toastId);
    if (toast) {
      toast.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => toast.remove(), 300);
    }
  }

  /**
   * Clear all toasts
   */
  clearAll() {
    const container = document.getElementById(this.containerId);
    if (container) {
      container.innerHTML = '';
    }
  }
}

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
  
  @media (max-width: 640px) {
    #toast-container {
      left: 1rem !important;
      right: 1rem !important;
      top: 1rem !important;
    }
  }
`;
document.head.appendChild(style);

// Singleton instance
const toast = new ToastManager();

export default toast;
